import { DataStore } from 'aws-amplify/datastore';
import {
  CustomDeck, Folder, FolderItemStatus, FolderItemType, FolderStatus, UserDocumentVersionSettings, UserNotationsStatus,
} from '@alucio/aws-beacon-amplify/src/models';
import type { CustomDeckGroup, PageSettingOverride } from '@alucio/aws-beacon-amplify/src/models';
import isEqual from 'lodash/isEqual';

export interface AIDeckSaveSession {
  deck?: CustomDeck;
  savedDeck?: CustomDeck;
  settings?: UserDocumentVersionSettings;
  folderItemId: string;
}

export interface SaveAIDeckInput {
  folderId: string;
  session: AIDeckSaveSession;
  existingFolderItemId?: string;
  existingSettingsId?: string;
  existingDeck?: CustomDeck;
  userId: string;
  tenantId: string;
  title: string;
  groups: CustomDeckGroup[];
  pageSettings: PageSettingOverride[];
}

// Retain generated model instances across retries: Amplify owns model IDs.
// This path is for owned My Folders only; shared destinations retain their existing services.
export const saveAIDeck = async (input: SaveAIDeckInput): Promise<void> => {
  const { userId, tenantId, existingDeck, title, groups, pageSettings } = input;
  const folder = await DataStore.query(Folder, input.folderId);
  if (!folder || folder.createdBy !== userId || folder.tenantId !== tenantId || folder.status !== FolderStatus.ACTIVE) {
    throw new Error('The destination folder is no longer available for editing.');
  }
  const existingFolderItem = input.existingFolderItemId
    ? folder.items?.find(item => item.id === input.existingFolderItemId)
    : undefined;
  if (input.existingFolderItemId && (!existingFolderItem || existingFolderItem.status !== FolderItemStatus.ACTIVE ||
    existingFolderItem.type !== FolderItemType.CUSTOM_DECK || existingFolderItem.itemId !== existingDeck?.id)) {
    throw new Error('The presentation is no longer available in this folder.');
  }
  const priorId = existingDeck?.id ?? input.session.deck?.id;
  const currentDeck = priorId ? await DataStore.query(CustomDeck, priorId) : undefined;
  if (existingDeck && !currentDeck) throw new Error('The presentation is no longer available.');
  if (currentDeck && (currentDeck.createdBy !== userId || currentDeck.tenantId !== tenantId)) {
    throw new Error('The presentation is no longer available for editing.');
  }
  const mutex = currentDeck?.editMutex;
  if (mutex && mutex.userId !== userId && Date.now() - new Date(mutex.lastSeenAt).getTime() < 15 * 60 * 1000) {
    throw new Error('The presentation is being edited by someone else.');
  }
  if (existingDeck && currentDeck && (
    (!isEqual(currentDeck.groups, existingDeck.groups) || currentDeck.title !== existingDeck.title) &&
    (!isEqual(currentDeck.groups, groups) || currentDeck.title !== title) &&
    (!input.session.savedDeck || !isEqual(currentDeck.groups, input.session.savedDeck.groups) ||
      currentDeck.title !== input.session.savedDeck.title)
  )) {
    throw new Error('The presentation changed. Reopen it before adding these slides.');
  }
  const now = new Date().toISOString();
  const deck = currentDeck
    ? CustomDeck.copyOf(currentDeck, draft => {
      draft.title = title;
      draft.groups = groups;
      draft.updatedAt = now;
      draft.updatedBy = userId;
      draft.autoUpdateAcknowledgedAt = now;
      // Match the standard save fallback when the online unlock cannot run.
      if (draft.editMutex) {
        draft.editMutex = {
          ...draft.editMutex,
          lastSeenAt: new Date(Date.parse(now) - 16 * 60 * 1000).toISOString(),
        };
      }
    })
    : input.session.deck
      ? CustomDeck.copyOf(input.session.deck, draft => { draft.title = title; draft.groups = groups; })
      : new CustomDeck({
        title,
        groups,
        tenantId,
        createdBy: userId,
        updatedBy: userId,
        createdAt: now,
        updatedAt: now,
        autoUpdateAcknowledgedAt: now,
      });
  input.session.deck = deck;
  const deckId = deck.id;

  const shouldSaveSettings = !!(pageSettings.length || input.existingSettingsId || input.session.settings);
  const settingsId = input.existingSettingsId ?? input.session.settings?.id;
  const settings = settingsId ? await DataStore.query(UserDocumentVersionSettings, settingsId) : undefined;
  if (input.existingSettingsId && !settings && !input.session.settings) {
    throw new Error('The presentation visibility settings are no longer available.');
  }
  if (settings && (
    settings.createdBy !== userId || settings.tenantId !== tenantId || settings.customDeckId !== deckId
  )) {
    throw new Error('The presentation visibility settings are no longer available.');
  }

  const settingsToSave = shouldSaveSettings
    ? settings
      ? UserDocumentVersionSettings.copyOf(settings, draft => {
        draft.pageSettings = pageSettings;
        draft.updatedAt = now;
        draft.updatedBy = userId;
      })
      : input.session.settings
        ? UserDocumentVersionSettings.copyOf(input.session.settings, draft => { draft.pageSettings = pageSettings; })
        : new UserDocumentVersionSettings({
          customDeckId: deckId,
          pageSettings,
          status: UserNotationsStatus.ACTIVE,
          tenantId,
          createdBy: userId,
          updatedBy: userId,
          createdAt: now,
          updatedAt: now,
        })
    : undefined;
  if (settingsToSave && (
    settingsToSave.createdBy !== userId || settingsToSave.tenantId !== tenantId ||
    settingsToSave.customDeckId !== deckId
  )) {
    throw new Error('The presentation visibility settings are no longer available.');
  }

  const savedDeck = await DataStore.save(deck);
  input.session.savedDeck = savedDeck;

  if (settingsToSave) {
    input.session.settings = settingsToSave;
    await DataStore.save(settingsToSave);
  }

  // Re-read after saving the deck/settings so unrelated folder additions are not dropped.
  const latestFolder = await DataStore.query(Folder, input.folderId);
  const latestExistingFolderItem = input.existingFolderItemId
    ? latestFolder?.items?.find(item => item.id === input.existingFolderItemId)
    : undefined;
  if (!latestFolder || latestFolder.createdBy !== userId || latestFolder.tenantId !== tenantId ||
    latestFolder.status !== FolderStatus.ACTIVE || (input.existingFolderItemId && (
    !latestExistingFolderItem || latestExistingFolderItem.status !== FolderItemStatus.ACTIVE ||
      latestExistingFolderItem.type !== FolderItemType.CUSTOM_DECK || latestExistingFolderItem.itemId !== deckId
  ))) {
    throw new Error('The destination folder is no longer available for editing.');
  }
  await DataStore.save(Folder.copyOf(latestFolder, draft => {
    const items = latestFolder.items ?? [];
    const exists = items.some(item => item.itemId === deckId && item.type === FolderItemType.CUSTOM_DECK);
    draft.items = exists
      ? items.map(item => item.itemId === deckId
        ? { ...item, customTitle: title, itemLastUpdatedAt: now }
        : item)
      : [...items, {
        id: input.session.folderItemId,
        type: FolderItemType.CUSTOM_DECK,
        itemId: deckId,
        status: FolderItemStatus.ACTIVE,
        addedAt: now,
        itemLastUpdatedAt: now,
        customTitle: title,
      }];
    draft.updatedAt = now;
    draft.updatedBy = userId;
  }));
};
